import { useCallback, useEffect, useRef, useState } from 'react';

import { useAgent } from '~/contexts/AgentContext';
import { ContentAst } from '~/eds/src/types/ContentAst';
import {
  parseContentAst,
  serializeContentAst,
} from '~/eds/src/utils/contentAst';

import type { AgentId } from '../../chat-bot-panel/agentIds';

export interface AgentMenuPosition {
  top: number;
  left: number;
}

const MENU_HEIGHT = 200;
const MENU_OFFSET = 8;
const FALLBACK_POSITION: AgentMenuPosition = { top: 100, left: 100 };
const POSITIONING_DELAY_MS = 10;
const CLICK_OUTSIDE_DELAY_MS = 100;

export const getAtSymbolInfo = (
  text: string,
): {
  shouldShowMenu: boolean;
  searchQuery: string;
} => {
  if (!text || typeof text !== 'string') {
    return { shouldShowMenu: false, searchQuery: '' };
  }

  const normalizedText = text.replace(/\n/g, ' ').trim();
  const atIndex = normalizedText.lastIndexOf('@');
  if (atIndex === -1) {
    return { shouldShowMenu: false, searchQuery: '' };
  }

  const afterAt = normalizedText.slice(atIndex + 1);
  const hasWhitespaceAfter = /^\s/.test(afterAt);
  const isAtEnd = afterAt === '';

  if (hasWhitespaceAfter || isAtEnd) {
    return { shouldShowMenu: true, searchQuery: '' };
  }

  const searchMatch = afterAt.match(/^([^\s]+)/);
  const searchQuery = searchMatch ? searchMatch[1] : '';

  return { shouldShowMenu: true, searchQuery };
};

export const removeAtSymbolFromContent = (content: ContentAst): ContentAst => {
  const text = serializeContentAst(content);
  const atIndex = text.lastIndexOf('@');
  if (atIndex === -1) {
    return content;
  }

  const afterAt = text.slice(atIndex + 1);
  const whitespaceIndex = afterAt.search(/\s/);
  const endIndex =
    whitespaceIndex === -1 ? text.length : atIndex + 1 + whitespaceIndex;

  const newText = text.slice(0, atIndex) + text.slice(endIndex);
  return parseContentAst(newText);
};

export const addAtSymbolToContent = (content: ContentAst): ContentAst => {
  const text = serializeContentAst(content);
  const newText = text + '@';
  return parseContentAst(newText);
};

interface UseAgentMenuOptions {
  editorContainerRef: React.RefObject<HTMLDivElement>;
  onContentChange?: (content: ContentAst) => void;
}

interface UseAgentMenuReturn {
  showAgentMenu: boolean;
  atSymbolPosition: AgentMenuPosition | null;
  searchQuery: string;
  openMenu: (position?: AgentMenuPosition) => void;
  closeMenu: () => void;
  handleContentChange: (content: ContentAst) => void;
  handleSelectAgent: (agentId: AgentId, currentContent: ContentAst) => void;
  positionMenuFromEditor: () => boolean;
}

export const useAgentMenu = ({
  editorContainerRef,
  onContentChange,
}: UseAgentMenuOptions): UseAgentMenuReturn => {
  const agentContext = useAgent();
  const [showAgentMenu, setShowAgentMenu] = useState(false);
  const [atSymbolPosition, setAtSymbolPosition] =
    useState<AgentMenuPosition | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const positioningTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const closeMenu = useCallback(() => {
    setShowAgentMenu(false);
    setAtSymbolPosition(null);
    setSearchQuery('');
  }, []);

  const positionMenuFromEditor = useCallback((): boolean => {
    if (editorContainerRef.current) {
      const editorRect = editorContainerRef.current.getBoundingClientRect();
      const spaceAbove = editorRect.top;

      const position: AgentMenuPosition = {
        top:
          spaceAbove >= MENU_HEIGHT + MENU_OFFSET
            ? editorRect.top - MENU_OFFSET
            : editorRect.bottom + MENU_OFFSET,
        left: editorRect.left + MENU_OFFSET,
      };
      setAtSymbolPosition(position);
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openMenu = useCallback(
    (position?: AgentMenuPosition) => {
      if (position) {
        setAtSymbolPosition(position);
        setShowAgentMenu(true);
      } else {
        const positioned = positionMenuFromEditor();
        if (!positioned) {
          setAtSymbolPosition(FALLBACK_POSITION);
        }
        setShowAgentMenu(true);
      }
    },
    [positionMenuFromEditor],
  );

  const handleContentChange = useCallback(
    (content: ContentAst) => {
      onContentChange?.(content);

      const text = serializeContentAst(content);
      const { shouldShowMenu, searchQuery: query } = getAtSymbolInfo(text);
      setSearchQuery(query);

      if (shouldShowMenu) {
        const tryPositionMenu = () => {
          const positioned = positionMenuFromEditor();
          if (!positioned) {
            setAtSymbolPosition(FALLBACK_POSITION);
          }
          setShowAgentMenu(true);
        };

        if (editorContainerRef.current) {
          tryPositionMenu();
        } else {
          if (positioningTimeoutRef.current) {
            clearTimeout(positioningTimeoutRef.current);
          }
          positioningTimeoutRef.current = setTimeout(
            tryPositionMenu,
            POSITIONING_DELAY_MS,
          );
        }
      } else {
        closeMenu();
        setSearchQuery('');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [positionMenuFromEditor, closeMenu, onContentChange],
  );

  useEffect(() => {
    return () => {
      if (positioningTimeoutRef.current) {
        clearTimeout(positioningTimeoutRef.current);
      }
    };
  }, []);

  const handleSelectAgent = useCallback(
    (agentId: AgentId, currentContent: ContentAst) => {
      agentContext?.selectAgent(agentId);
      closeMenu();

      const updatedContent = removeAtSymbolFromContent(currentContent);
      onContentChange?.(updatedContent);
    },
    [agentContext, closeMenu, onContentChange],
  );

  useEffect(() => {
    if (!showAgentMenu) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element;

      if (target?.closest('[data-agent-menu]')) {
        return;
      }

      if (target?.closest('[data-at-button]')) {
        return;
      }

      closeMenu();
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, CLICK_OUTSIDE_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [showAgentMenu, closeMenu]);

  return {
    showAgentMenu,
    atSymbolPosition,
    searchQuery,
    openMenu,
    closeMenu,
    handleContentChange,
    handleSelectAgent,
    positionMenuFromEditor,
  };
};
